import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function useStrategies() {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/strategies");
      setStrategies(res.data?.strategies || res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  const createStrategy = useCallback(async (data) => {
    try {
      await api.post("/strategies", data);
      toast.success("Strategy created");
      await refetch();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create strategy");
      return false;
    }
  }, [refetch]);

  const updateStrategy = useCallback(async (id, data) => {
    try {
      await api.put(`/strategies/${id}`, data);
      toast.success("Strategy updated");
      await refetch();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update strategy");
      return false;
    }
  }, [refetch]);

  const deleteStrategy = useCallback(async (id) => {
    try {
      await api.delete(`/strategies/${id}`);
      toast.success("Strategy deleted");
      await refetch();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete strategy");
      return false;
    }
  }, [refetch]);

  const toggleStrategy = useCallback(async (id, enabled) => {
    try {
      await api.put(`/strategies/${id}`, { enabled });
      toast.success(enabled ? "Strategy enabled" : "Strategy paused");
      await refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to toggle strategy");
    }
  }, [refetch]);

  const runStrategy = useCallback(async (id) => {
    try {
      const res = await api.post(`/strategies/${id}/run`);
      toast.success("Strategy triggered");
      return res.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to run strategy");
      throw e;
    }
  }, []);

  return {
    strategies, loading, error, refetch,
    createStrategy, updateStrategy, deleteStrategy, toggleStrategy, runStrategy,
  };
}
